import type { RosterAgent } from "@ark-bot/protocol";
import { createContext, useContext } from "react";

import { formatLocale } from "./i18n.js";
import { isNewWorkspace } from "./onboarding.js";

/**
 * The zone the page shows every time in: the one the host keeps
 * (`settings.timeZone`) and the agents are told, which a new box takes from
 * the first device that opens it and the user can change in Settings; or
 * this device's own while none is set.
 */

/** This device's zone, as the browser reports it. */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Every zone the browser knows, for the picker; the given ones are kept even if it does not list them. */
export function timeZoneChoices(...keep: ReadonlyArray<string | null | undefined>): string[] {
  let known: string[] = [];
  try {
    known = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    // An old browser without the list: the picker still offers the zones in hand.
  }
  const all = new Set(known);
  for (const zone of ["UTC", ...keep]) if (zone != null && zone.length > 0) all.add(zone);
  return [...all].sort((a, b) => a.localeCompare(b));
}

/** "UTC+7", "UTC-4:30", "UTC": the zone's offset at `at`, as the agents are told it. */
export function offsetOf(zone: string, at = Date.now()): string {
  try {
    const name = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(at).find((part) => part.type === "timeZoneName")?.value ?? "GMT";
    return name === "GMT" ? "UTC" : name.replace("GMT", "UTC");
  } catch {
    return "";
  }
}

/** "Asia/Jakarta (UTC+7)", with the underscores of the IANA name read as spaces. */
export function zoneLabel(zone: string, at = Date.now()): string {
  const offset = offsetOf(zone, at);
  const name = zone.replace(/_/g, " ");
  return offset.length > 0 ? `${name} (${offset})` : name;
}

/** Whether this browser can format times in `zone`; anything else would throw at every time on the page. */
export function isKnownZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** "UTC", "Etc/UTC", "GMT" and the other names of the same clock. */
export function isUtcZone(zone: string): boolean {
  return /^(Etc\/)?(UTC|UCT|GMT|Universal|Zulu|Greenwich)([+-]?0)?$/i.test(zone);
}

/** What the page should do about the box's zone on first sight, once it knows enough to say. */
export type ZoneDecision = "wait" | "apply" | "leave";

export interface ZoneInput {
  /** The host's settings as read; "failed" when they could not be. */
  readonly settings: "loading" | "failed" | { readonly timeZone?: string | undefined };
  /** The roster's first full snapshot; null until it has arrived. */
  readonly roster: readonly RosterAgent[] | null;
  /** This device's zone. */
  readonly device: string;
}

/**
 * A new box takes the zone of the first device that opens it, so nobody has
 * to be asked for one: the page sets it once, on a box whose zone was never
 * set and where nobody has talked yet (onboarding.ts, as the tour decides).
 * A box already in use keeps UTC until the user picks, because its routines
 * were scheduled on that clock and a new zone would move every one of them.
 * A device that says UTC is passed over: that is far more often a browser
 * hiding its zone, or the box's own desktop, than someone who lives on UTC,
 * and writing it would stop the next device from filling in the real one.
 * Once only: the prototype pushes the detected zone on every connect, but a
 * box here is opened from many devices, and each would pull it to its own.
 */
export function zoneDecision({ settings, roster, device }: ZoneInput): ZoneDecision {
  if (settings === "failed") return "leave";
  if (settings !== "loading" && settings.timeZone != null) return "leave";
  if (isUtcZone(device)) return "leave";
  if (settings === "loading" || roster == null) return "wait";
  return isNewWorkspace(roster) ? "apply" : "leave";
}

export interface TimeZoneState {
  /** The zone every time on the page is shown in. */
  readonly zone: string;
  /** The zone the user picked, or null while the page is showing this device's. */
  readonly chosen: string | null;
  /** Picks a zone: the host keeps it for every device, and the agents use it from their next message. */
  readonly choose: (zone: string) => Promise<void>;
}

export const TimeZoneContext = createContext<TimeZoneState>({ zone: deviceTimeZone(), chosen: null, choose: async () => undefined });

export function useTimeZone(): TimeZoneState {
  return useContext(TimeZoneContext);
}

/**
 * The same zone for code that formats a time outside a component, like a
 * bubble's hover title. The app sets it as it renders, before its children,
 * so they read the zone the context holds.
 */
let current: string | undefined;

export function mirrorTimeZone(zone: string): void {
  current = zone;
}

/** "14:05" in the zone the page shows. */
export function displayTime(at: number): string {
  return new Date(at).toLocaleTimeString(formatLocale(), { timeZone: current, hour: "2-digit", minute: "2-digit" });
}

/** The full date and time in the zone the page shows, for a hover title. */
export function displayDateTime(at: number): string {
  return new Date(at).toLocaleString(formatLocale(), { timeZone: current });
}
